/* eslint-disable */
import { Input, Typography } from 'antd';
import { FormInstance } from 'antd/lib/form';
import dayjs from 'dayjs';
import { get } from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Button,
  Checkbox,
  DateInput,
  Form,
  PhoneNumberFormItem,
  SalesFilterSelect,
  Select,
} from '~/components';
import { useHandbook } from '~/controllers/handbook';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { AgentLeadPayload, AgentLeadStatus } from '~/types/AgentLead';
import { CompanyTypeReq } from '~/types/CompanyType';
import { NameFn } from '~/utils/buildNamePath';
import createUid from '~/utils/createUid';
import { handleCompanyType } from '~/utils/handleCompanyType';
import { maxLength } from '~/utils/lengthValidation';
import phoneValidation from '~/utils/phoneValidation';
import phoneValidationWithPrefix from '~/utils/phoneValidationWithPrefix';
import { textOnlyValidation } from '~/utils/textOnlyValidation';
import { validateCharacters } from '~/utils/validateCharacters';

import EmailFormItem from '../../../components/inputs/EmailFormItem';

interface Props {
  onFinish?: (values: AgentLeadPayload) => any;
  n: NameFn;
  disabled?: boolean;
  form: FormInstance<AgentLeadPayload>;
  status?: AgentLeadStatus;
  terminationReason?: string;
  terminationDate?: string;
  initialValues?: any;
  edit?: boolean;
  formName?: string;
}

const LeadAddForm: React.FC<Props> = (props) => {
  const {
    onFinish,
    n,
    disabled = false,
    form,
    status,
    initialValues,
    edit,
    formName: customFormName,
  } = props;

  const { t } = useTranslation('sales');
  const handbookQuery = useHandbook();
  const handbook = handbookQuery.data?.handbooks;

  const onValuesChange = (changedValues: CompanyTypeReq, formValues: any) => {
    if (!handbook) return;
    handleCompanyType(form, handbook, n)(changedValues, formValues);
  };

  // Derive initialValues: map ngeniusOnline or ecom → ecom so checkbox shows checked for either
  const derivedInitialValues = React.useMemo(() => {
    if (!initialValues?.products) return initialValues;
    const { ngeniusOnline, ecom, ...restProducts } = initialValues.products;
    return {
      ...initialValues,
      products: {
        ...restProducts,
        ecom: !!(ngeniusOnline || ecom),
      },
    };
  }, [initialValues]);

  // Handle form submission - add +966 prefix to phone number
  const handleFinish = (values: any) => {
    // Add +966 prefix to phone number if it doesn't have a country code
    let businessPhone = values.businessPhone;
    if (businessPhone && !businessPhone.startsWith('+')) {
      businessPhone = `966${businessPhone}`;
    }

    // Remap camelCase product keys → display keys to match update API format
    const rawProducts = values.products || {};
    const normalizedProducts = {
      'ECOM': !!(rawProducts['ecom'] || rawProducts['ECOM'] || rawProducts["ngeniusOnline"] || rawProducts["NGenius Online"]),
      'Pay Links': !!(rawProducts['payLinks'] || rawProducts['Pay Links']),
      'POS': !!(rawProducts['pos'] || rawProducts['POS']),
      'Other': !!(rawProducts['other'] || rawProducts['Other']),
    };

    const transformedValues = {
      ...values,
      businessPhone,
      products: normalizedProducts,
    };
    onFinish?.(transformedValues);
  };

  console.log('initialValues', initialValues);
  // @ts-ignore
  return (
    <Container className="LeadAdd">
      <Form
        form={form}
        name={customFormName || formName}
        onFinish={handleFinish}
        onValuesChange={onValuesChange}
        initialValues={derivedInitialValues}
      >
        <Section>
          <InputsGroupLayout>
            {get(initialValues, 'leadId') && (
              <Form.Item
                name={n(['leadNo'])}
                disabled
                initialValue="NI-86721"
                labelText={t('Lead No.')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            )}
            <Form.Item
              labelText={t('Channel')}
              name={n(['channel'])}
              initialValue={get(initialValues, 'channel') || 'Manual'}
              disabled
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            {get(initialValues, 'channel') === 'SOB DROPPED JOURNEY' && (
              <Form.Item
                labelText={t('Stage')}
                name={n(['stage'])}
                disabled
                rules={[{ required: true }]}
              >
                <SalesFilterSelect filterCriteria="stage" allowClear />
              </Form.Item>
            )}
            {get(initialValues, 'utmSource') && (
              <Form.Item
                labelText={t('UTM-Source')}
                name={n(['utmSource'])}
                disabled
                rules={[{ required: true }]}
              >
                <SalesFilterSelect filterCriteria="utmSource" allowClear />
              </Form.Item>
            )}
            {get(initialValues, 'utmMedium') && (
              <Form.Item
                labelText={t('UTM-Medium')}
                name={n(['utmMedium'])}
                disabled
                rules={[{ required: true }]}
              >
                <SalesFilterSelect filterCriteria="utmMedium" allowClear />
              </Form.Item>
            )}
            {edit && (
              <>
                <Form.Item labelText={t('Campaign Name')} name={n(['campaignName'])} disabled>
                  <SalesFilterSelect filterCriteria="campaignName" allowClear />
                </Form.Item>
                <Form.Item labelText={t('Ad Group Name')} name={n(['adGroupName'])} disabled>
                  <SalesFilterSelect filterCriteria="adGroupName" allowClear />
                </Form.Item>
                <Form.Item labelText={t('Landing Page')} name={n(['landingPage'])} disabled>
                  <Input />
                </Form.Item>
                <Form.Item labelText={t('Referral')} name={n(['referral'])} disabled>
                  <Input />
                </Form.Item>
                <Form.Item labelText={t('Device')} name={n(['device'])} disabled>
                  <Input />
                </Form.Item>
                <Form.Item labelText={t('GeoLocation')} name={n(['geoLocation'])} disabled>
                  <Input />
                </Form.Item>
              </>
            )}
            <Form.Item
              labelText={t('Company Name')}
              name={n(['companyName'])}
              rules={[
                {
                  required: true,
                },
                { validator: validateCharacters({ mustIncludesLetters: true }) },
                { validator: maxLength(100) },
              ]}
              disabled={disabled || get(initialValues, 'companyName')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              labelText={t('First Name')}
              name={n(['firstName'])}
              rules={[
                { required: true },
                {
                  validator: textOnlyValidation({
                    withInnerSpaces: true,
                  }),
                },
                { validator: maxLength(40) },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              labelText={t('Last Name')}
              name={n(['lastName'])}
              rules={[
                { required: true },
                {
                  validator: textOnlyValidation({
                    withInnerSpaces: true,
                  }),
                },
                { validator: maxLength(60) },
              ]}
            >
              <Input />
            </Form.Item>
            <EmailFormItem
              labelText="Business Email"
              name={n(['businessEmail'])}
              rules={[
                { required: true },
                { type: 'email', message: t('Invalid email', { ns: 'validation' }) },
              ]}
            />

            <Form.Item
              labelText="Phone No"
              name={n(['businessPhone'])}
              rules={[{ required: true }, { validator: phoneValidationWithPrefix }]}
            >
              <Input addonBefore="+966" />
            </Form.Item>

            <Form.Item
              labelText={t('Est. Monthly Transaction Value [SAR]')}
              name={n(['monthTransactionValue'])}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
          <Section>
            <Typography.Title level={4}>{t('Products').toUpperCase()}</Typography.Title>
            <Typography.Paragraph>
              <Trans ns="agent" defaults="Select the products that merchant is interested in" />
            </Typography.Paragraph>
            {[
              {
                id: 'leadAddProductPOS',
                name: n(['products', 'ecom']),
                title: t('ECOM'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
              {
                id: 'leadAddProductECommerce',
                name: n(['products', 'payLinks']),
                title: t('Plugins'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
              {
                id: 'leadAddProductPOSNew',
                name: n(['products', 'pos']),
                title: t('POS'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
              {
                id: 'leadAddProductSoftPOS',
                name: n(['products', 'other']),
                title: t('Other'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
            ].map(({ id, name, title, isDisabled }) => {
              return (
                <Form.ShouldUpdate
                  key={id}
                  shouldUpdate={(prev, next) =>
                    Object.values(prev.products || {}).filter((enabled) => enabled).length !==
                    Object.values(next.products || {}).filter((enabled) => enabled).length
                  }
                >
                  {(newForm) => {
                    const formValues = newForm.getFieldsValue();
                    return (
                      <Form.Item
                        name={name}
                        valuePropName="checked"
                        className="LeadForm-AddProduct"
                      // disabled={disabled || isDisabled(formValues)}
                      >
                        <Checkbox
                          onChange={() => {
                            void form.validateFields([n(['productsValidator'])]);
                          }}
                        >
                          {title}
                        </Checkbox>
                      </Form.Item>
                    );
                  }}
                </Form.ShouldUpdate>
              );
            })}
            <Form.Item
              name={n(['productsValidator'])}
              rules={[
                {
                  validator: () => {
                    const products = form.getFieldValue(n(['products'])) || {};
                    const hasSelectedProduct = Object.values(products).some((val) => val === true);
                    if (!hasSelectedProduct) {
                      return Promise.reject(new Error(t('Please select at least one product')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <div style={{ display: 'none' }} />
            </Form.Item>
          </Section>
          <Form.Item labelText={'Comments'} name={n(['comments'])} disabled={edit}>
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
        </Section>

        <Section>
          <InputsGroupLayout>
            <Form.Item
              labelText={t('Status')}
              name={n(['leadStatus'])}
              rules={[{ required: true }]}
            >
              <Select
                options={
                  [
                    { value: 'active', labelText: 'Active' },
                    { value: 'new', labelText: 'New' },
                    { value: 'contacted', labelText: 'Contacted' },
                    { value: 'inProgress', labelText: 'In Progress' },
                    { value: 'convertedtoApplication', labelText: 'Converted to Application' },
                    { value: 'rejected', labelText: 'Rejected' },
                  ] as any
                }
              />
            </Form.Item>
            <Form.Item
              labelText={t('New/Exist')}
              name={n(['newExist'])}
              rules={[{ required: true }]}
            >
              <Select
                options={
                  [
                    { value: 'new', labelText: 'New' },
                    { value: 'exist', labelText: 'Exist' },
                  ] as any
                }
              />
            </Form.Item>
            <Form.Item
              labelText={t('Contacted Date')}
              name={n(['contactedDate'])}
            // rules={[{ required: true }]}
            >
              <DateInput disableDateRule="date <= today" />
            </Form.Item>
            <Form.Item
              labelText={t('Follow Up Date')}
              name={n(['followUpDate'])}
            // rules={[{ required: true }]}
            >
              <DateInput disableDateRule="date <= today" />
            </Form.Item>
            <Form.Item
              hidden
              labelText={t('Last Modification Date & Time')}
              name={n(['lastModificationDate&Time'])}
              rules={[{ required: true }, { type: 'date' }]}
              initialValue={new Date()?.toISOString()}
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
        </Section>

        <Section id="activityLogs">
          <Typography.Title level={4}>{t('Activities Log').toUpperCase()}</Typography.Title>
          <div className="activities-log-container">
            <Form.List name={n(['activityLogs'])}>
              {(fields, { add, remove }) => (
                <>
                  <div className="activities-log-header">
                    <div className="activity-column">Date</div>
                    <div className="activity-column">Activity Type</div>
                    <div className="activity-column">Comments</div>
                    <div className="activity-actions">
                      <Button
                        id="add-activity-header-btn"
                        type="primary"
                        shape="circle"
                        size="small"
                        className="add-activity-btn"
                        onClick={() => add()}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                  {fields.map(({ key, name, ...restField }) => {
                    const activityLog = get(initialValues, ['activityLogs', name]);
                    const hasExistingData =
                      edit &&
                      activityLog &&
                      (activityLog.date || activityLog.activityType || activityLog.comments);

                    return (
                      <div key={key} className="activity-log-row">
                        <div className="activity-column">
                          <Form.Item
                            {...restField}
                            name={[name, 'date']}
                            rules={[{ required: false }]}
                          >
                            <DateInput disabled={hasExistingData} />
                          </Form.Item>
                        </div>
                        <div className="activity-column">
                          <Form.Item
                            {...restField}
                            name={[name, 'activityType']}
                            rules={[{ required: false }]}
                          >
                            <Input placeholder="Activity Type" disabled={hasExistingData} />
                          </Form.Item>
                        </div>
                        <div className="activity-column">
                          <Form.Item
                            {...restField}
                            name={[name, 'comments']}
                            rules={[{ required: false }]}
                          >
                            <Input placeholder="Comments" />
                          </Form.Item>
                        </div>
                        <div className="activity-actions">
                          <Button
                            id={`remove-activity-btn-${key}`}
                            type="text"
                            size="small"
                            onClick={() => remove(name)}
                            className="remove-activity-btn"
                            disabled={hasExistingData}
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </Form.List>
          </div>
        </Section>

        <Section>
          <InputsGroupLayout>
            <Form.Item
              labelText={t('Conversion Date')}
              name={n(['conversionDate'])}
            // rules={[{ required: true }]}
            >
              <DateInput disableDateRule="date <= today" />
            </Form.Item>
            <Form.Item
              labelText={t('Conversion Type')}
              name={n(['conversionType'])}
            // rules={[{ required: true }]}
            >
              <Select
                options={
                  [
                    { value: 'Onboarding Completed', labelText: 'Onboarding Completed' },
                    { value: 'Sale Made', labelText: 'Sale Made' },
                  ] as any
                }
              />
            </Form.Item>
            <Form.Item
              labelText={t('Time to Conversation')}
              name={n(['timetoConversion'])}
              disabled
            >
              <Input />
            </Form.Item>
            <Form.Item labelText={t('Result')} name={n(['result'])} rules={[{ required: true }]}>
              <SalesFilterSelect filterCriteria={'result'} allowClear />
            </Form.Item>
          </InputsGroupLayout>
        </Section>

        <Section>
          <Form.Item labelText={'Remarks'} name={n(['salesRemark'])}>
            <Input.TextArea autoSize={{ minRows: 1 }} />
          </Form.Item>
        </Section>
      </Form>
    </Container>
  );
};
export const formName = 'leadForm';

const Container = styled.div`
  .LeadForm-AddProduct {
    margin-bottom: 8px;

    span {
      font-weight: 500;
    }
  }

  .LeadAdd-TerminationReason {
    margin-bottom: 8px;
  }

  .LeadAdd-TerminationButton {
    margin-bottom: 24px;
  }

  .activities-log-container {
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
  }

  .activities-log-header {
    display: grid;
    grid-template-columns: 1fr 1fr 2fr auto;
    gap: 1px;
    background-color: #fafafa;
    border-bottom: 1px solid #d9d9d9;
    font-weight: 600;
    font-size: 14px;

    .activity-column {
      padding: 12px 16px;
      border-right: 1px solid #d9d9d9;
      display: flex;
      align-items: center;

      &:last-child {
        border-right: none;
      }
    }

    .activity-actions {
      padding: 8px 16px;
      display: flex;
      align-items: center;
      justify-content: center;

      .add-activity-btn {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        line-height: 1;
      }
    }
  }

  .activity-log-row {
    display: grid;
    grid-template-columns: 1fr 1fr 2fr auto;
    gap: 1px;
    border-bottom: 1px solid #d9d9d9;

    &:last-child {
      border-bottom: none;
    }

    .activity-column {
      padding: 8px 16px;
      border-right: 1px solid #d9d9d9;
      display: flex;
      align-items: center;

      &:last-child {
        border-right: none;
      }

      .ant-form-item {
        margin-bottom: 0;
        width: 100%;

        .ant-form-item-control-input {
          min-height: auto;
        }
      }
    }

    .activity-actions {
      padding: 8px 16px;
      display: flex;
      align-items: center;
      justify-content: center;

      .remove-activity-btn {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        line-height: 1;
        color: #ff4d4f;

        &:hover {
          color: #ff7875;
          background-color: #fff2f0;
        }
      }
    }
  }
`;

export default LeadAddForm;
